import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Linkedin, Twitter } from 'lucide-react';

interface TeamMemberProps {
  name: string;
  position: string;
  image: string;
  delay: number;
}

const TeamMember: React.FC<TeamMemberProps> = ({ name, position, image, delay }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.5, delay: delay * 0.1 }}
      className="group relative"
    >
      <div className="relative aspect-[3/4] overflow-hidden rounded-3xl bg-gray-100">
        <img 
          src={image} 
          alt={name} 
          className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500">
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <p className="text-white/80 text-sm mb-4 transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 delay-100">
              Connect with {name.split(' ')[0]} on social media
            </p>
            <div className="flex space-x-3 transform translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 delay-200">
            <a href="#" className="bg-white/10 backdrop-blur-sm p-2 rounded-lg hover:bg-white/20 transition-colors">
              <Linkedin size={18} className="text-white" />
            </a>
            <a href="#" className="bg-white/10 backdrop-blur-sm p-2 rounded-lg hover:bg-white/20 transition-colors">
              <Twitter size={18} className="text-white" />
            </a>
            </div>
          </div>
        </div>
        <div className="absolute inset-0 ring-1 ring-black/10 rounded-3xl"></div>
      </div>
      <div className="mt-6">
        <h3 className="font-sans text-xl font-semibold tracking-tight">{name}</h3>
        <p className="text-gray-600 font-medium">{position}</p>
      </div>
    </motion.div>
  );
};

const Team: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const team = [
    {
      name: "Arjun Sharma",
      position: "Chief Investment Officer",
      image: "https://images.pexels.com/photos/3778603/pexels-photo-3778603.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
    },
    {
      name: "Meera Patel",
      position: "Head of Portfolio Management",
      image: "https://images.pexels.com/photos/5717577/pexels-photo-5717577.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
    },
    {
      name: "Vikram Malhotra",
      position: "Senior Financial Advisor",
      image: "https://images.pexels.com/photos/2379005/pexels-photo-2379005.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
    },
    {
      name: "Sunita Reddy",
      position: "Tax Strategy Specialist",
      image: "https://images.pexels.com/photos/3808822/pexels-photo-3808822.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2"
    }
  ];

  return (
    <section id="team" className="py-20 bg-gray-50">
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-black text-white text-sm font-medium mb-8">
            Our Leadership
          </span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-8">Meet Our Team</h2>
          <p className="text-xl text-gray-600">
            Our team of experienced financial experts is dedicated to delivering exceptional 
            results and personalized service to each client.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {team.map((member, index) => (
            <TeamMember
              key={index}
              name={member.name}
              position={member.position}
              image={member.image}
              delay={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Team;